import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility as ViewIcon,
  VisibilityOff as HiddenIcon,
  KeyboardArrowUp as SortUpIcon,
  KeyboardArrowDown as SortDownIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDatabase, ref, get, query, orderByChild, limitToLast } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { TestResult } from '../types/testTypes';

const TestResults: React.FC = () => {
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<keyof TestResult>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTestResults = async () => {
      if (!currentUser) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Fetch from Firebase Realtime Database
        const db = getDatabase();
        const testResultsRef = ref(db, `users/${currentUser.uid}/grids`);
        
        // Get all test results (both old push() IDs and new UUID-based IDs)
        const snapshot = await get(testResultsRef);
        const results: TestResult[] = [];
        
        if (snapshot.exists()) {
          snapshot.forEach((childSnapshot) => {
            const data = childSnapshot.val();
            results.push({
              id: childSnapshot.key || '',
              userId: data.userId,
              timestamp: data.timestamp,
              eye: data.eyeTested,
              markedPoints: data.dots || data.markedPoints || [],
              abnormalities: data.abnormalities || [],
              notes: data.notes || '',
            });
          });
        }
        
        // Sort results based on timestamp and sortDirection
        if (sortDirection === 'asc') {
          results.sort((a, b) => a.timestamp - b.timestamp);
        } else {
          results.sort((a, b) => b.timestamp - a.timestamp);
        }
        
        setTestResults(results);
      } catch (err) {
        console.error('Error fetching test results:', err);
        setError('Failed to load test results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTestResults();
  }, [currentUser, sortDirection]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: keyof TestResult) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'MMM dd, yyyy');
    } catch (err) {
      console.error('Error formatting date:', err);
      return 'Invalid date';
    }
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'h:mm a');
    } catch (err) {
      console.error('Error formatting time:', err);
      return '';
    }
  };

  const handleViewTestResult = (testId: string) => {
    navigate(`/test-results/${testId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h1">
          Test History
        </Typography>
        <Button 
          variant="contained" 
          color="primary"
          onClick={() => navigate('/test')}
        >
          Take a New Test
        </Button>
      </Box>
      
      {testResults.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography align="center">
            You haven't taken any Amsler Grid tests yet. Take your first test to start monitoring your vision.
          </Typography>
          <Box display="flex" justifyContent="center" mt={2}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/test')}
            >
              Take a Test Now
            </Button>
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="test results table">
              <TableHead>
                <TableRow>
                  <TableCell 
                    onClick={() => handleSort('timestamp')} 
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box display="flex" alignItems="center">
                      Date
                      {sortField === 'timestamp' && (
                        sortDirection === 'asc' ? <SortUpIcon /> : <SortDownIcon />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell 
                    onClick={() => handleSort('eye')} 
                    sx={{ cursor: 'pointer' }}
                  >
                    <Box display="flex" alignItems="center">
                      Eye
                      {sortField === 'eye' && (
                        sortDirection === 'asc' ? <SortUpIcon /> : <SortDownIcon />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Results</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? testResults.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : testResults
                ).map((test) => (
                  <TableRow
                    key={test.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {formatDate(test.timestamp)}
                    </TableCell>
                    <TableCell>{formatTime(test.timestamp)}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {test.eye === 'left' ? (
                          <>
                            <HiddenIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Left
                          </>
                        ) : (
                          <>
                            <ViewIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Right
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {test.abnormalities && test.abnormalities.length > 0 ? (
                        <Chip 
                          label={`${test.abnormalities.length} abnormalities`} 
                          color="error" 
                          size="small"
                        />
                      ) : (
                        <Chip 
                          label="Normal" 
                          color="success" 
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View details">
                        <IconButton onClick={() => handleViewTestResult(test.id)}>
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, { label: 'All', value: -1 }]}
            component="div"
            count={testResults.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};

export default TestResults; 